import { alertKind } from "../../../shared/alerts";
import { AQI_CATEGORIES, POLLUTANT_NAMES } from "../../../shared/air";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf } from "../../data/queries";
import { useAir, useAlerts } from "../../data/weatherQueries";
import { Icon } from "../../ui/Icon";
import { EmptyState } from "../../ui/StatusBadge";
import { useNow } from "../../ui/time";
import { NeedsLocation, remPx, SkyFoot } from "../sky/common";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import { ArcGauge } from "./gauges";
import { AQI_BANDS, aqiFraction } from "./scales";
import "./nerd.css";

type AirConfig = Record<string, never>;

function AirQuality({ size, fullscreen, openFullscreen }: WidgetProps<AirConfig>) {
  const { settings, timeZone } = useHousehold();
  useNow("minute");
  const q = useAir();
  const alerts = useAlerts().data?.alerts.filter((a) => alertKind(a.event) === "air") ?? [];
  const state = dataStateOf(q, 3 * 60 * 60_000);
  if (!settings.location) return <NeedsLocation icon="eye" title="Air quality needs a home location" detail="Set a general location to see the air quality index." compact={size.variant === "compact"} />;
  const data = q.data;
  if (!data) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="eye" title="Air quality unavailable" detail={q.error?.message} />;
  if (data.status === "unavailable") return <EmptyState icon="eye" title="Air quality" detail={data.reason} />;

  const top = data.readings[0];
  const cat = top ? AQI_CATEGORIES[top.category] ?? AQI_CATEGORIES[1]! : null;
  const compact = size.height < remPx() * 11.3;
  const dayLabel = (date: string, i: number) => (i === 0 ? "Today" : i === 1 ? "Tomorrow" : new Intl.DateTimeFormat("en-US", { timeZone: "UTC", weekday: "short" }).format(new Date(`${date}T12:00:00Z`)));
  void timeZone;

  return (
    <div className="nerd nerd-air">
      {!compact && <WidgetHeader title="Air quality" meta={data.reportingArea ?? undefined} onTitle={fullscreen ? undefined : openFullscreen} />}
      {alerts[0] && (
        <div className="air-alert">
          <Icon name="alert" size="1rem" /> {alerts[0].event}
        </div>
      )}
      {!top || !cat ? (
        <p className="muted">No air quality reading nearby right now.</p>
      ) : (
        <div className="air-now">
          <span className="air-dial">
            <ArcGauge value={aqiFraction(top.aqi)} bands={AQI_BANDS} label={`Air quality index ${top.aqi}, ${cat.name}`}>
              <strong className="g-val">{top.aqi}</strong>
              <span className="g-unit">AQI</span>
            </ArcGauge>
          </span>
          <span className="air-text">
            <strong>{cat.name}</strong>
            <span className="muted">{POLLUTANT_NAMES[top.parameter] ?? top.parameter} is the main pollutant</span>
            {!compact && <span className="air-advice">{cat.advice}</span>}
          </span>
        </div>
      )}
      {!compact && data.forecast.length > 0 && (
        <div className="air-forecast">
          {data.forecast.slice(0, 2).map((f, i) => {
            const c = AQI_CATEGORIES[f.category];
            return (
              <span key={f.date} className="air-day" style={c ? { ["--aqi" as string]: c.color } : undefined}>
                <b>{dayLabel(f.date, i)}</b>
                <i />
                {c ? c.name : "No forecast"}
                {f.aqi !== null ? ` · ${f.aqi}` : ""}
                {f.actionDay && <em>Action day</em>}
              </span>
            );
          })}
        </div>
      )}
      {!compact && <SkyFoot source="EPA AirNow" stamp={data.stamp} state={state} updatedAt={q.dataUpdatedAt} />}
    </div>
  );
}

export const airQualityWidget: WidgetDefinition<AirConfig> = {
  type: "air-quality",
  version: 1,
  name: "Air quality",
  description: "Today’s Air Quality Index near home (ozone, smoke and dust), with EPA health advice, tomorrow’s forecast and any air quality alert.",
  icon: "eye",
  category: "weather",
  defaultSize: { w: 4, h: 3 },
  minSize: { w: 2, h: 2 },
  stackHeight: 280,
  defaults: {},
  settings: [],
  provider: "airnow",
  capabilities: { requiresLocation: true },
  supportsFullscreen: true,
  Component: AirQuality,
};
